import React from 'react';
import Icon from '../../../components/AppIcon';

const PaymentFormCanvas = ({ formElements, onElementSelect, selectedElementId, onElementDelete, paymentConfig }) => {
  const handleDrop = (e) => {
    e?.preventDefault();
    // Handle drop logic if needed
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const renderPaymentElement = (element) => {
    const isSelected = element?.id === selectedElementId;

    const baseClasses = `
      relative p-4 rounded-md border-2 transition-smooth cursor-pointer
      ${isSelected 
        ? 'border-primary bg-primary/5' :'border-transparent hover:border-border hover:bg-muted/30'
      }
    `;

    const renderElementContent = () => {
      switch (element?.type) {
        case 'payment-card':
          return (
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Icon name="CreditCard" size={16} />
                Card Information
                <span className="text-destructive">*</span>
              </label>
              <div className="p-4 bg-muted/50 border border-border rounded-md">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-muted-foreground">Powered by Stripe</span>
                  <div className="flex gap-2">
                    <Icon name="CreditCard" size={16} className="text-muted-foreground" />
                    <Icon name="Wallet" size={16} className="text-muted-foreground" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-10 bg-background border border-input rounded-md flex items-center px-3 text-sm text-muted-foreground">
                    1234 5678 9012 3456
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-10 bg-background border border-input rounded-md flex items-center px-3 text-sm text-muted-foreground">
                      MM / YY
                    </div>
                    <div className="h-10 bg-background border border-input rounded-md flex items-center px-3 text-sm text-muted-foreground">
                      CVC
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );

        case 'payment-amount':
          return (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Icon name="DollarSign" size={16} />
                Amount
                <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="text"
                  className="w-full pl-8 pr-3 py-2 bg-background border border-input rounded-md text-foreground"
                  placeholder="0.00"
                  disabled
                />
              </div>
              <p className="text-xs text-muted-foreground">Enter the amount you wish to pay</p>
            </div>
          );

        case 'billing-address':
          return (
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Icon name="Home" size={16} />
                Billing Address
                <span className="text-destructive">*</span>
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm"
                  placeholder="Street address"
                  disabled
                />
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm"
                  placeholder="Apt, suite, etc. (optional)"
                  disabled
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    className="px-3 py-2 bg-background border border-input rounded-md text-sm"
                    placeholder="City"
                    disabled
                  />
                  <input
                    type="text"
                    className="px-3 py-2 bg-background border border-input rounded-md text-sm"
                    placeholder="State"
                    disabled
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    className="px-3 py-2 bg-background border border-input rounded-md text-sm"
                    placeholder="ZIP code"
                    disabled
                  />
                  <input
                    type="text"
                    className="px-3 py-2 bg-background border border-input rounded-md text-sm"
                    placeholder="Country"
                    disabled
                  />
                </div>
              </div>
            </div>
          );

        case 'subscription-plan':
          return (
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Icon name="Layers" size={16} />
                Choose Your Plan
                <span className="text-destructive">*</span>
              </label>
              <div className="space-y-2">
                {['Basic - $9/mo', 'Pro - $29/mo', 'Enterprise - $99/mo']?.map((plan, idx) => (
                  <div
                    key={idx}
                    className={`p-3 border-2 rounded-md cursor-pointer transition-smooth ${
                      idx === 1 ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">{plan}</span>
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        idx === 1 ? 'border-primary bg-primary' : 'border-input'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

        case 'order-summary':
          return (
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Icon name="FileText" size={16} />
                Order Summary
              </label>
              <div className="p-4 bg-muted/30 border border-border rounded-md space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-foreground font-medium">$99.00</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="text-foreground font-medium">$8.91</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="text-foreground font-medium">$5.00</span>
                </div>
                <div className="pt-2 border-t border-border flex justify-between">
                  <span className="text-sm font-semibold text-foreground">Total</span>
                  <span className="text-lg font-bold text-primary">$112.91</span>
                </div>
              </div>
            </div>
          );

        case 'security-badge':
          return (
            <div className="flex items-center justify-center gap-4 p-4 bg-muted/30 border border-border rounded-md">
              <div className="flex items-center gap-2">
                <Icon name="Lock" size={20} className="text-green-600" />
                <span className="text-xs font-medium text-foreground">SSL Encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="ShieldCheck" size={20} className="text-blue-600" />
                <span className="text-xs font-medium text-foreground">Secured by Stripe</span>
              </div>
            </div>
          );

        default:
          return (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {element?.label || element?.name}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm"
                placeholder={element?.placeholder || 'Enter value...'}
                disabled
              />
            </div>
          );
      }
    };

    return (
      <div
        key={element?.id}
        className={baseClasses}
        onClick={() => onElementSelect(element)}
      >
        {renderElementContent()}
        
        {isSelected && (
          <div className="absolute -top-2 -right-2 flex gap-1">
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onElementDelete(element?.id);
              }}
              className="p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-smooth"
            >
              <Icon name="Trash2" size={14} />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="min-h-full p-8 bg-muted/20"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <div className="max-w-2xl mx-auto">
        {/* Payment Form Header */}
        <div className="mb-6 p-6 bg-card border border-border rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold text-foreground">Secure Payment</h2>
            <div className="flex items-center gap-2">
              <Icon name="Lock" size={16} className="text-green-600" />
              <span className="text-xs text-muted-foreground">Encrypted</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Complete your payment securely. All transactions are encrypted and processed by Stripe.
          </p>
        </div>

        {/* Form Elements */}
        <div className="space-y-4 bg-card border border-border rounded-lg p-6">
          {formElements?.length === 0 ? (
            <div className="py-16 text-center">
              <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                Start Building Your Payment Form
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Drag and drop payment elements from the left panel to create a secure checkout experience.
              </p>
            </div>
          ) : (
            formElements?.map(element => renderPaymentElement(element))
          )}

          {/* Submit Button Preview */}
          {formElements?.length > 0 && (
            <div className="pt-4 border-t border-border">
              <button className="w-full py-3 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2">
                <Icon name="Lock" size={16} />
                Complete Payment
              </button>
              <p className="text-xs text-center text-muted-foreground mt-3">
                By completing this payment, you agree to our terms and conditions
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentFormCanvas;